<script lang="ts" setup generic="O = any, V = any">
import { computed } from 'vue';
import { toString } from 'lodash-es';
import { IconCircle, IconCircleDotFilled } from '@/icons';
import type { Path } from '../../../shared/types';
import { getValue } from '../../../shared/utils';
import { useOptionsList } from '../../composables';
import type { OptionsListProps } from '../../shared/types';
type Props = OptionsListProps<O, V> & {
  modelValue?: V | null;
  optionDescription?: Path<O> | ((option: O) => V);
  vertical?: boolean;
};
defineOptions({
  inheritAttrs: false,
});
const {
  options = [],
  asyncOptions,
  optionDisabled,
  optionLabel,
  optionValue,
  hydrateValue,
  trackValueBy,
  optionsLimit,
  optionDescription,
  vertical,
} = defineProps<Props>();
defineEmits<{
  'update:modelValue': [value: V | null];
}>();
const modelValue = defineModel<V | null>({ default: null });
const { optionsList, selectOption } = useOptionsList<O, V>({
  asyncOptions,
  options: computed(() => options),
  optionDisabled,
  optionLabel,
  optionValue,
  hydrateValue,
  trackValueBy,
  selectionMode: 'single',
  selected: modelValue,
  updateSelected(value) {
    modelValue.value = value as V | null;
  },
  optionsLimit,
});
const optionsListWithDescription = computed(() => {
  return optionsList.value.map((option) => {
    let description = null;
    if (optionDescription && option.origin) {
      description = getValue(option.origin, optionDescription);
    }
    return {
      ...option,
      description: description ? toString(description) : null,
    };
  });
});
</script>

<template>
  <fieldset :class="[$style.root, { [$style.vertical]: vertical }]">
    <label
      v-for="option in optionsListWithDescription"
      :key="option.label"
      :class="[$style.option, { [$style.selected]: option.selected }]"
    >
      <input
        :class="$style.input"
        type="radio"
        :value="option.value"
        :checked="option.selected"
        @change="selectOption(option)"
      />
      <div :class="$style.icon">
        <IconCircleDotFilled v-if="option.selected" aria-hidden="true" size="20" />
        <IconCircle v-else aria-hidden="true" size="20" />
      </div>
      <div :class="$style.content">
        <div :class="$style.label">
          {{ option.label }}
        </div>
        <div v-if="option.description" :class="$style.description">
          {{ option.description }}
        </div>
      </div>
    </label>
  </fieldset>
</template>

<style lang="scss" module>
@use '../../styles/shared';

.root {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(calc(25% - 12px), 1fr));
  padding: 0;
  margin: 0;
  border: none;

  &.vertical {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

.option {
  position: relative;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 8px;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  &.selected {
    background: rgba(0, 0, 0, 0.04);
  }
}

.icon {
  color: #d2d3d9;
  width: 20px;
  height: 20px;
  flex: none;

  > svg {
    width: 100%;
    height: 100%;
  }
}

.selected .icon {
  color: #010000;
}

.content {
  display: grid;
  gap: 8px;
}

.label {
  color: #010000;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  padding-top: 2px;
}

.description {
  color: #978f91;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.input {
  @include shared.sr-only();
}
</style>
